import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
const _excluded = ["error", "clearable", "getValidationMessage", "icon", "onChange", "placeholder", "readOnly", "value", "onKeyDown", "styled"];
import unescapedText from 'I18n/utils/unescapedText';
import { memo, useEffect, useState } from 'react';
import { syntheticEvent } from '../../utils/SyntheticEvent/SyntheticEvent';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { callIfPossible } from '../../utils/functions/functions';
import { TextInput } from '../../components/TextInput/TextInput';
import { lazyEval } from '../../utils/lazyEval';
import { RemoveIcon, SearchIcon } from 'foundations-assets';
import * as React from 'react';
import { computeMinimumSearchMessageOverflow } from '../../utils/computeMinimumSearchMessageOverflow';
import { css } from 'styled-components';
import styled from 'foundations-theming/utils';
import { Clickable } from '../Clickable/Clickable';
import { StyledInput } from '../TextInput/TextInput';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getMinimumSearchMessage = (value, getValidationMessage) => {
  if (getValidationMessage) {
    return getValidationMessage(value);
  }
  const valueLength = value ? value.length : 0;
  const charsNeeded = valueLength;
  if (valueLength && charsNeeded > 0) {
    return unescapedText('salesUI.UISearchInput.minimumSearchMessage', {
      count: charsNeeded
    });
  }
  return null;
};
const Wrapper = styled.div.withConfig({
  displayName: "SearchInput__Wrapper",
  componentId: "urqj2j-0"
})(({
  theme: _theme,
  $cssStyles
}) => {
  const theme = _theme;
  return Object.assign({
    position: 'relative',
    display: 'inline-block',
    width: '100%',
    [`& ${StyledInput}`]: {
      paddingRight: theme.space['1000'] // TODO : This is 40px, need a token for 44px
    }
  }, $cssStyles);
});
const MessageWrapper = styled.span.withConfig({
  displayName: "SearchInput__MessageWrapper",
  componentId: "urqj2j-1"
})(() => {
  return {
    position: 'absolute',
    bottom: '1px',
    pointerEvents: 'none',
    right: '0',
    top: '1px'
  };
});
const MessageInner = styled.span.withConfig({
  displayName: "SearchInput__MessageInner",
  componentId: "urqj2j-2"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    color: theme.color['icon-core-default'],
    whiteSpace: 'nowrap'
  };
});
const IconWrapper = styled.span.withConfig({
  displayName: "SearchInput__IconWrapper",
  componentId: "urqj2j-3"
})(({
  theme: _theme,
  $showClearButton
}) => {
  const theme = _theme;
  return {
    color: theme.color['icon-core-default'],
    display: 'inline-block',
    wordBreak: 'normal',
    marginInline: theme.space['400'],
    marginBlockStart: '11px',
    // TODO : token for 11px
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    visibility: $showClearButton ? 'hidden' : 'visible'
  };
});
export const getStyledClickableStyles = props => {
  const {
    theme: _theme,
    $cssStyles
  } = props;
  const theme = _theme;
  const styles = css(["position:absolute;right:1px;top:1px;padding-block-start:", ";padding-block-end:", ";padding-inline:", ";line-height:16px;font-size:16px;", ""], theme.space['300'], theme.space['400'], theme.space['400'], Object.assign({}, $cssStyles));
  return styles;
};
const StyledClickable = styled(_ref => {
  let rest = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/_jsx(Clickable, Object.assign({}, rest));
}).withConfig({
  displayName: "SearchInput__StyledClickable",
  componentId: "urqj2j-4"
})(["", ";"], props => getStyledClickableStyles(props));
const TYPES = ['text', 'email', 'number', 'password', 'search', 'url', 'tel'];
export const MemoLessSearchInput = _ref2 => {
  let {
      error = false,
      clearable = true,
      getValidationMessage,
      icon = true,
      onChange,
      placeholder = unescapedText('salesUI.UISearchInput.placeholder'),
      readOnly,
      value: propValue,
      onKeyDown,
      styled: _styled = true
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded);
  const [minimumSearchMessageOverflows, setMinimumSearchMessageOverflows] = useState(false);
  const [inputValue, setInputValue] = useState(propValue || '');
  const wrapperRef = React.useRef(null);
  const iconRef = React.useRef(null);
  const helpTextRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const showClearButton = clearable && inputValue && !readOnly;
  const minimumSearchMessage = getMinimumSearchMessage(inputValue, getValidationMessage);
  const triggerClear = () => {
    var _inputRef$current;
    setInputValue('');
    callIfPossible(onChange, syntheticEvent(''));
    inputRef === null || inputRef === void 0 || (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
  };
  const handleKeyDown = evt => {
    if (readOnly) return;
    if (evt.key === 'Escape') {
      triggerClear();
    }
    callIfPossible(onKeyDown, evt);
  };
  useEffect(() => {
    setInputValue(propValue || '');
  }, [propValue]);
  useEffect(() => {
    if (getValidationMessage > 0) {
      const overflowResult = computeMinimumSearchMessageOverflow(wrapperRef.current, iconRef.current, helpTextRef.current, inputRef.current);
      if (typeof overflowResult === 'boolean') {
        setMinimumSearchMessageOverflows(overflowResult);
      } else {
        setMinimumSearchMessageOverflows(false);
      }
    }
  }, [getValidationMessage, inputRef]);
  const handleInputChange = event => {
    const newValue = event.target.value;
    setInputValue(newValue);
    if (onChange) {
      onChange(event);
    }
  };
  const {
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(Tooltip, {
    open: !!(minimumSearchMessage && minimumSearchMessageOverflows),
    title: minimumSearchMessage,
    children: /*#__PURE__*/_jsxs(Wrapper, {
      $cssStyles: cssStyles,
      ref: wrapperRef,
      children: [/*#__PURE__*/_jsx(TextInput, Object.assign({
        onKeyDown: handleKeyDown,
        placeholder: lazyEval(placeholder),
        readOnly: readOnly,
        onChange: handleInputChange,
        value: inputValue,
        helpTextRef: helpTextRef,
        iconRef: iconRef,
        inputRef: inputRef,
        error: error,
        use: "default",
        type: "text",
        role: "searchbox",
        styled: _styled
      }, getValidationMessage ? {
        minimumSearchMessage
      } : {}, rest)), /*#__PURE__*/_jsxs(MessageWrapper, {
        children: [getValidationMessage && minimumSearchMessage ? /*#__PURE__*/_jsx(MessageInner, {
          ref: helpTextRef,
          children: minimumSearchMessage
        }) : null, icon && !readOnly ? /*#__PURE__*/_jsx(IconWrapper, {
          $showClearButton: showClearButton,
          ref: iconRef,
          children: /*#__PURE__*/_jsx(SearchIcon, {
            size: "300",
            color: "fill-secondary-pressed"
          })
        }) : null]
      }), showClearButton ? /*#__PURE__*/_jsx(StyledClickable, {
        "aria-label": "Clear input",
        onClick: triggerClear,
        tabIndex: 0,
        children: /*#__PURE__*/_jsx(RemoveIcon, {
          size: "300",
          color: "icon-core-default"
        })
      }) : null]
    })
  });
};
export const SearchInput = /*#__PURE__*/memo(MemoLessSearchInput);